import { useCallback, useEffect, useMemo, useState } from "react";
import * as S from "./styles";
import Chart from "react-apexcharts";
import {
  LiveComment,
  MatchData,
  MatchDetailsOdds,
} from "@/interfaces/liveGamesType";
import ReactDOMServer from "react-dom/server";
import { CircularProgress, Grid, useTheme } from "@mui/material";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { fetchLiveOddsByMatch } from "@/hooks/fetchHooks/liveOddsFetch";
import { useAuth } from "@/context/AuthContext";
import { fakeLiveOddsData } from "@/pages/Dashboard/V2/components/MatchDetails/fakeData";

interface AttackMomentumProps {
  isFinished?: boolean;
  matchData: MatchData;
}

interface ExtraMatchDetails extends MatchDetailsOdds {
  homeImg: string;
  awayImg: string;
}

function convert2Seconds(time: string) {
  const [minutos, segundos] = time.split(":").map(Number);
  return minutos * 60 + segundos;
}

function convert2FootballTime(seconds: number) {
  const convertedSec = Math.round(Math.abs(seconds));

  const minutos = Math.floor(convertedSec / 60);
  const segundosRestantes = convertedSec % 60;
  return `${minutos}:${segundosRestantes < 10 ? "0" : ""}${segundosRestantes}`;
}

export default function AttackMomentum({ isFinished, matchData }: AttackMomentumProps) {
  const {isAuthenticated, decodedToken} = useAuth()
  // Substituindo chamada direta à API pelo hook
  const { 
    data: liveOddsData, 
    isLoading
  } = isAuthenticated && decodedToken.isActive ? fetchLiveOddsByMatch(matchData.match_id, {
    refetchInterval: isFinished ? false : 60000,
    enabled: !isFinished,
    meta: { errorMessage: "Ocorreu um erro ao buscar os dados do gráfico de posse de bola" },
  }): {
    data: fakeLiveOddsData,
    isLoading: false
  };

  const theme  = useTheme();
  const formattedTime = useMemo(() => {
    return liveOddsData?.live_comments
      ? liveOddsData.live_comments
        .sort((a, b) => convert2Seconds(a.time) - convert2Seconds(b.time))
        .map((el, i) => {
          if (i === liveOddsData.live_comments.length - 1) {
            return 0;
          }

          if (
            el.text.includes("Half time") ||
            el.text.includes("Full time")
          ) {
            return 0;
          }

          const nextIndex =
          liveOddsData?.live_comments.length - 1 === i ? i : i + 1;
          const nextGame = liveOddsData.live_comments[nextIndex];

          if (el.text.includes(liveOddsData.match_hometeam_name)) {
            return convert2Seconds(nextGame.time) - convert2Seconds(el.time);
          }

          if (el.text.includes(liveOddsData.match_awayteam_name)) {
            return -(
              convert2Seconds(nextGame.time) - convert2Seconds(el.time)
            );
          }

          return 0;
        })
      : [];
  }, [liveOddsData]);

  return (
    <>
      {isLoading ? (
        <Grid container justifyContent="center" my={2}>
          <CircularProgress />
        </Grid>
      ) : formattedTime.length ? (
        <S.GraphContainer>
          <h3>Gráfico Posse de bola</h3>
          <S.ChartWrapper>
            <S.ChartImages>
              <img src={matchData.team_home_badge} onError={(e) => {
                e.currentTarget.src = `https://ui-avatars.com/api/?name=${matchData.match_hometeam_name}`;
              }}/>
              <img src={matchData.team_away_badge} onError={(e) => {
                e.currentTarget.src = `https://ui-avatars.com/api/?name=${matchData.match_awayteam_name}`;
              }}/>
            </S.ChartImages>
            <S.ChartContainer>
              <Chart
                options={{
                  chart: {
                    toolbar: {
                      show: false
                    }
                  },
                  tooltip: {
                    theme: "dark",
                    
                    custom: function ({
                      series,
                      seriesIndex,
                      dataPointIndex,
                      w,
                    }) {
                      // 'w' é o objeto global do gráfico, você pode acessar os dados brutos com w.config.series[seriesIndex].data[dataPointIndex]
                      const barElement = w.globals.dom.baseEl.querySelector(
                        ".apexcharts-bar-series"
                      ).childNodes[seriesIndex].childNodes[dataPointIndex];

                      // Obtenha a cor da barra diretamente do estilo computado
                      const barColor = window.getComputedStyle(barElement).fill;

                      return ReactDOMServer.renderToString(
                        <S.TooltipContainer color={barColor}>
                          <div></div>
                          <p>
                            Tempo de posse:{" "}
                            {convert2FootballTime(
                              w.config.series[seriesIndex].data[dataPointIndex]
                            )}
                          </p>
                        </S.TooltipContainer>
                      );
                    },
                  },
                  plotOptions: {
                    bar: {
                      colors: {
                        ranges: [
                          {
                            from: -7000,
                            to: -1,
                            color: theme.palette.awayColor.main,
                          },
                          {
                            from: 0,
                            to: 7000,
                            color: theme.palette.homeColor.main,
                          },
                        ],
                      },
                      columnWidth: "80%",
                    },
                  },
                  dataLabels: {
                    enabled: false,
                  },
                  yaxis: {
                    show: false,
                    // labels: {
                    //   formatter: function (y) {
                    //     return y.toFixed(0) + "%";
                    //   },
                    // },
                  },
                  xaxis: {
                    categories: [""],
                  },
                }}
                series={[
                  {
                    data: formattedTime,
                  },
                ]}
                type="bar"
              />
            </S.ChartContainer>
          </S.ChartWrapper>
        </S.GraphContainer>
      ) : (
        <S.StatsNotFound>
          <p>Gráfico Não Disponível</p>
        </S.StatsNotFound>
      )}
    </>
  );
}
